import { action, computed, makeObservable, observable, runInAction } from "mobx";
import RootStore from "store/rootStore";
import constants from "tools/constants";
import {
  extractVideoLabelsFromModelPrediction,
  normalizeApiModel,
} from "tools/modelPredictions";
import {
  chartDurationSecFromExact,
  probeVideoDurationSec,
} from "tools/videoDuration";

export type AnalyseModel = {
  id: string;
  name: string;
  version: string;
  description?: string;
};

export const ANALYSE_INCIDENTS_SUPPORTED_LABELING_TYPE = "VIDEO";

export type AnalyseProject = {
  id: string;
  name: string;
  labelingType?: string;
};

export type AnalyseBatch = {
  id: number;
  name: string;
  projectId: string;
};

export type AnalyseVideo = {
  id: string;
  name: string;
  /** Durée arrondie pour les axes du graphe (secondes). */
  durationSec: number;
  /** Durée exacte en secondes (ex. 9.233), pour l'affichage. */
  durationExactSec: number;
  fps: number;
};

export type AnalyseIncidentClass = {
  id: string | number;
  name: string;
  color: string;
};

export type AnalyseIncidentInterval = {
  id: string;
  className: string;
  classColor: string;
  startTime: number;
  endTime: number;
};

export type AnalyseIncidentSeries = {
  classes: AnalyseIncidentClass[];
  labelerIntervals: AnalyseIncidentInterval[];
  modelIntervals: AnalyseIncidentInterval[];
  secondaryModelIntervals: AnalyseIncidentInterval[];
};

const PALETTE = constants.colors.map((c) => c.color);

const MOCK_CLASS_NAMES = [
  "Véhicule arrêté",
  "Contresens",
  "Fumée",
  "Objet",
  "Congestion",
];

// Pseudo-random deterministic generator (mulberry32) for stable mocks.
const createRng = (seed: number) => {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const hashString = (value: string) => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

const buildMockClasses = (): AnalyseIncidentClass[] =>
  MOCK_CLASS_NAMES.map((name, idx) => ({
    id: idx + 1,
    name,
    color: PALETTE[idx % PALETTE.length],
  }));

const parseTimeToSeconds = (timeStr: string): number => {
  const [minutesPart, rest] = timeStr.split(":");
  const [secondsPart, msPart] = (rest || "0").split(".");
  return (
    parseInt(minutesPart, 10) * 60 +
    parseInt(secondsPart, 10) +
    parseInt(msPart || "0", 10) / 1000
  );
};

const maxEndTimeFromVideoLabels = (videoLabels: any[]): number =>
  (videoLabels || []).reduce((acc: number, vl: any) => {
    if (!vl?.endTime) return acc;
    return Math.max(acc, parseTimeToSeconds(vl.endTime));
  }, 0);

const durationFromLabelsFallback = (media: any): number | null => {
  const labelerLabels: any[] = media?.labels?.videoLabels || [];
  let maxEnd = maxEndTimeFromVideoLabels(labelerLabels);

  const predictionEntries: any[] = media?.modelPredictions || [];
  predictionEntries.forEach((entry: any) => {
    const predLabels =
      entry?.labels?.videoLabels || entry?.videoLabels || [];
    maxEnd = Math.max(maxEnd, maxEndTimeFromVideoLabels(predLabels));
  });

  return maxEnd > 0 ? maxEnd : null;
};

const mapMediaToAnalyseVideo = (
  media: any,
  index: number,
  cachedDurationSec?: number
): AnalyseVideo => {
  const id = String(media.id);
  if (cachedDurationSec != null && cachedDurationSec > 0) {
    return {
      id,
      name: media.name || `Media ${index + 1}`,
      durationExactSec: cachedDurationSec,
      durationSec: chartDurationSecFromExact(cachedDurationSec),
      fps: media.fps || 25,
    };
  }
  return {
    id,
    name: media.name || `Media ${index + 1}`,
    durationSec: 1,
    durationExactSec: 0,
    fps: media.fps || 25,
  };
};

const mapTagsToClasses = (rawTags: any[]): AnalyseIncidentClass[] =>
  rawTags.map((tag: any, index: number) => ({
    id: typeof tag === "string" ? index : tag.id,
    name: typeof tag === "string" ? tag : tag.name,
    color: PALETTE[index % PALETTE.length],
  }));

const mapVideoLabelsToIntervals = (
  videoLabels: any[],
  classes: AnalyseIncidentClass[]
): AnalyseIncidentInterval[] =>
  (videoLabels || []).map((vl: any, index: number) => {
    const matched =
      classes.find((c) => c.name === vl.tagName) ||
      classes.find((c) => String(c.id) === String(vl.tagId));
    const className = vl.tagName || matched?.name || "Incident";
    const classColor = matched?.color || PALETTE[index % PALETTE.length];
    const startTime = parseTimeToSeconds(vl.startTime || "0:00.000");
    const endTime = parseTimeToSeconds(vl.endTime || vl.startTime || "0:00.000");
    return {
      id: `labeler-${index}-${startTime}`,
      className,
      classColor,
      startTime,
      endTime: Math.max(endTime, startTime + 0.5),
    };
  });

const resolveProjectLabelingType = (project: any): string | undefined => {
  const fromTypes = project?.labelingTypes?.[0]?.name;
  if (typeof fromTypes === "string" && fromTypes.length > 0) {
    return fromTypes;
  }
  if (
    typeof project?.labelingType === "string" &&
    project.labelingType.length > 0
  ) {
    return project.labelingType;
  }
  return undefined;
};

const isVideoLabelingType = (labelingType?: string | null): boolean =>
  labelingType === ANALYSE_INCIDENTS_SUPPORTED_LABELING_TYPE;

const mapApiModelToAnalyseModel = (item: any): AnalyseModel => {
  const normalized = normalizeApiModel(item);
  return {
    id: normalized.id,
    name: normalized.name,
    version: normalized.version,
    description: item?.description,
  };
};

class AnalyseStore {
  @observable models: AnalyseModel[] = [];
  @observable projects: AnalyseProject[] = [];
  @observable batches: AnalyseBatch[] = [];
  @observable videos: AnalyseVideo[] = [];

  @observable selectedModelId: string | null = null;
  @observable selectedSecondaryModelId: string | null = null;
  @observable selectedProjectId: string | null = null;
  @observable selectedBatchId: number | null = null;
  @observable selectedVideoId: string | null = null;

  @observable incidentClasses: AnalyseIncidentClass[] = [];
  @observable incidentSeries: AnalyseIncidentSeries | null = null;

  @observable isLoadingModels: boolean = false;
  @observable isLoadingProjects: boolean = false;
  @observable isLoadingBatches: boolean = false;
  @observable isLoadingVideos: boolean = false;
  @observable isLoadingSeries: boolean = false;
  @observable errorMessage: string | null = null;

  private mediaRawById: Record<string, any> = {};
  private videoDurationCache: Record<string, number> = {};

  constructor(private store: RootStore) {
    makeObservable(this);
  }

  @action setSelectedModel = (modelId: string | null) => {
    this.selectedModelId = modelId;
    if (
      modelId != null &&
      this.selectedSecondaryModelId != null &&
      this.selectedSecondaryModelId === modelId
    ) {
      this.selectedSecondaryModelId = null;
    }
    this.incidentSeries = null;
  };

  @action setSelectedSecondaryModel = (modelId: string | null) => {
    this.selectedSecondaryModelId = modelId;
    this.incidentSeries = null;
  };

  @action setSelectedProject = (projectId: string | null) => {
    this.selectedProjectId = projectId;
    this.selectedBatchId = null;
    this.selectedModelId = null;
    this.selectedSecondaryModelId = null;
    this.selectedVideoId = null;
    this.batches = [];
    this.models = [];
    this.videos = [];
    this.mediaRawById = {};
    this.videoDurationCache = {};
    this.incidentClasses = [];
    this.incidentSeries = null;
  };

  @action setSelectedBatch = (batchId: number | null) => {
    this.selectedBatchId = batchId;
    this.selectedModelId = null;
    this.selectedSecondaryModelId = null;
    this.selectedVideoId = null;
    this.models = [];
    this.videos = [];
    this.mediaRawById = {};
    this.videoDurationCache = {};
    this.incidentClasses = [];
    this.incidentSeries = null;
  };

  @action setSelectedVideo = (videoId: string | null) => {
    this.selectedVideoId = videoId;
    this.incidentSeries = null;
  };

  @action applyParamsToSelection = (selection: {
    modelId: string | null;
    secondaryModelId?: string | null;
    projectId: string | null;
    batchId: number | null;
    mediaId: string | null;
  }) => {
    const batchChanged =
      selection.batchId != null && selection.batchId !== this.selectedBatchId;
    const mediaChanged =
      selection.mediaId != null && selection.mediaId !== this.selectedVideoId;

    if (batchChanged) {
      this.videos = [];
      this.mediaRawById = {};
      this.videoDurationCache = {};
      this.incidentSeries = null;
    } else if (mediaChanged || selection.mediaId == null) {
      this.incidentSeries = null;
    }

    this.selectedModelId = selection.modelId;
    const secondaryModelId =
      selection.secondaryModelId != null &&
      selection.secondaryModelId !== "" &&
      selection.secondaryModelId !== selection.modelId
        ? selection.secondaryModelId
        : null;
    this.selectedSecondaryModelId = secondaryModelId;
    this.selectedProjectId = selection.projectId;
    this.selectedBatchId = selection.batchId;
    this.selectedVideoId = selection.mediaId;
  };

  /** Modèles ayant des prédictions sur le lot (GET /api/prediction-models?batchId=). */
  @action fetchPredictionModels = async (appFetch: any, batchId: number) => {
    if (!batchId) {
      runInAction(() => {
        this.models = [];
      });
      return;
    }

    this.isLoadingModels = true;
    this.errorMessage = null;
    try {
      const response = await appFetch(
        `${constants.paths.PREDICTION_MODELS_PATH}?batchId=${encodeURIComponent(
          String(batchId)
        )}`
      );
      const content = Array.isArray(response)
        ? response
        : response?.content || [];
      const normalized: AnalyseModel[] = (Array.isArray(content) ? content : []).map(
        mapApiModelToAnalyseModel
      );
      runInAction(() => {
        this.models = normalized;
        if (
          this.selectedModelId != null &&
          !normalized.some((m) => m.id === this.selectedModelId)
        ) {
          this.selectedModelId = null;
          this.incidentSeries = null;
        }
        if (
          this.selectedSecondaryModelId != null &&
          !normalized.some((m) => m.id === this.selectedSecondaryModelId)
        ) {
          this.selectedSecondaryModelId = null;
          this.incidentSeries = null;
        }
      });
    } catch (e) {
      runInAction(() => {
        const message = (e as Error)?.message;
        this.errorMessage =
          message || "Impossible de charger les modeles de prediction";
        this.models = [];
        this.selectedModelId = null;
        this.selectedSecondaryModelId = null;
        this.incidentSeries = null;
      });
    } finally {
      runInAction(() => {
        this.isLoadingModels = false;
      });
    }
  };

  /**
   * Fetch the list of projects accessible to the current user.
   * Falls back to a mock list if the API is unavailable so the UI stays
   * usable while the analytics backend is not deployed yet.
   */
  @action fetchProjects = async (appFetch: any) => {
    this.isLoadingProjects = true;
    this.errorMessage = null;
    try {
      const response = await appFetch(
        `${constants.paths.USER_PROJECTS_PATH}?size=1000&sort=createdDate,desc`
      );
      const content = Array.isArray(response) ? response : response?.content || [];
      runInAction(() => {
        this.projects = content.map((p: any) => ({
          id: String(p.id),
          name: p.name,
          labelingType: resolveProjectLabelingType(p),
        }));
      });
    } catch (e) {
      runInAction(() => {
        const message = (e as Error)?.message;
        this.errorMessage = message || "Impossible de charger les projets";
        this.projects = this.buildFallbackProjects();
      });
    } finally {
      runInAction(() => {
        this.isLoadingProjects = false;
      });
    }
  };

  /**
   * Fetch batches for a given project. The list endpoint supports a
   * `projectId` filter, see `src/pages/batches/container.tsx`.
   */
  @action fetchBatches = async (appFetch: any, projectId: string) => {
    if (!projectId) return;
    this.isLoadingBatches = true;
    this.errorMessage = null;
    try {
      const response = await appFetch(
        `${constants.paths.BATCHES_PATH}?projectId=${projectId}&size=1000&sort=createdDate,desc`
      );
      const content = Array.isArray(response) ? response : response?.content || [];
      runInAction(() => {
        this.batches = content.map((b: any) => ({
          id: b.id,
          name: b.name,
          projectId: String(b.project?.id ?? projectId),
        }));
      });
    } catch (e) {
      runInAction(() => {
        const message = (e as Error)?.message;
        this.errorMessage = message || "Impossible de charger les lots";
        this.batches = this.buildFallbackBatches(projectId);
      });
    } finally {
      runInAction(() => {
        this.isLoadingBatches = false;
      });
    }
  };

  /**
   * Medias (images or videos) for a batch — same endpoint as labelerVideo.
   * @see labelVideoStore.fetchVideos
   */
  @action fetchVideos = async (appFetch: any, batchId: number) => {
    if (!batchId) return;
    this.isLoadingVideos = true;
    this.incidentSeries = null;
    this.errorMessage = null;
    try {
      const medias = await appFetch(
        `/api/images/batches/${batchId}/all?offset=0&size=1000`
      );
      const list = Array.isArray(medias) ? medias : [];
      runInAction(() => {
        const rawById: Record<string, any> = {};
        list.forEach((media: any) => {
          rawById[String(media.id)] = media;
        });
        this.mediaRawById = rawById;
        this.videos = list.map((media: any, index: number) =>
          mapMediaToAnalyseVideo(
            media,
            index,
            this.videoDurationCache[String(media.id)]
          )
        );
      });

      const selectedId = this.selectedVideoId;
      if (selectedId && this.mediaRawById[selectedId]) {
        await this.ensureVideoDuration(selectedId);
      }
    } catch (e) {
      runInAction(() => {
        const message = (e as Error)?.message;
        this.errorMessage =
          message || "Impossible de charger les medias du lot";
        this.videos = this.buildFallbackVideos(batchId);
      });
    } finally {
      runInAction(() => {
        this.isLoadingVideos = false;
      });
    }
  };

  @action private updateVideoDuration = (videoId: string, exactSec: number) => {
    this.videos = this.videos.map((video) =>
      video.id === videoId
        ? {
            ...video,
            durationExactSec: exactSec,
            durationSec: chartDurationSecFromExact(exactSec),
          }
        : video
    );
  };

  /**
   * Résout la durée réelle via les métadonnées du fichier vidéo (comme le labeler).
   * Retombe sur la fin du dernier label si la lecture échoue.
   */
  @action ensureVideoDuration = async (videoId: string): Promise<number | null> => {
    const cached = this.videoDurationCache[videoId];
    if (cached != null) {
      this.updateVideoDuration(videoId, cached);
      return cached;
    }

    const rawMedia = this.mediaRawById[videoId];
    if (!rawMedia) return null;

    let exactSec: number | null = null;
    if (typeof rawMedia.url === "string" && rawMedia.url.length > 0) {
      exactSec = await probeVideoDurationSec(rawMedia.url);
    }

    if (exactSec == null) {
      exactSec = durationFromLabelsFallback(rawMedia);
    }

    if (exactSec == null || exactSec <= 0) {
      exactSec = 60;
    }

    runInAction(() => {
      this.videoDurationCache[videoId] = exactSec as number;
      this.updateVideoDuration(videoId, exactSec as number);
    });

    return exactSec;
  };

  @action fetchBatchClasses = async (appFetch: any, batchId: number) => {
    if (!batchId) return;
    try {
      const batch = await appFetch(`${constants.paths.BATCHES_PATH}/${batchId}`);
      const labelingTypes = batch?.project?.labelingTypes?.[0];
      const rawTags: any[] = labelingTypes?.listTagsDto || [];
      const batchLabelingType =
        labelingTypes?.name ||
        resolveProjectLabelingType(batch?.project);
      runInAction(() => {
        this.incidentClasses =
          rawTags.length > 0 ? mapTagsToClasses(rawTags) : buildMockClasses();
        if (batchLabelingType && this.selectedProjectId) {
          this.projects = this.projects.map((project) =>
            project.id === this.selectedProjectId
              ? { ...project, labelingType: batchLabelingType }
              : project
          );
        }
      });
    } catch (e) {
      runInAction(() => {
        const message = (e as Error)?.message;
        this.errorMessage = message || "Impossible de charger les classes";
        this.incidentClasses = buildMockClasses();
      });
    }
  };

  @action fetchIncidentSeries = async (appFetch: any) => {
    const { selectedModelId, selectedVideoId, selectedBatchId } = this;
    if (selectedVideoId == null || selectedBatchId == null) {
      runInAction(() => {
        this.incidentSeries = null;
      });
      return;
    }

    if (!this.isSelectedProjectVideo) {
      runInAction(() => {
        this.incidentSeries = null;
      });
      return;
    }

    this.isLoadingSeries = true;
    this.errorMessage = null;

    try {
      await this.ensureVideoDuration(selectedVideoId);

      const video = this.videos.find((v) => v.id === selectedVideoId);
      if (!video || video.durationExactSec <= 0) {
        runInAction(() => {
          this.incidentSeries = null;
        });
        return;
      }

      if (this.incidentClasses.length === 0) {
        await this.fetchBatchClasses(appFetch, selectedBatchId);
      }

      const classes =
        this.incidentClasses.length > 0
          ? this.incidentClasses
          : buildMockClasses();

      const rawMedia = this.mediaRawById[selectedVideoId];
      const videoLabels: any[] = rawMedia?.labels?.videoLabels || [];
      const labelerIntervals = mapVideoLabelsToIntervals(videoLabels, classes);

      const selectedModel = this.models.find((m) => m.id === selectedModelId);
      const predictedLabels = selectedModel
        ? extractVideoLabelsFromModelPrediction(rawMedia, selectedModel)
        : [];
      const modelIntervals = mapVideoLabelsToIntervals(
        predictedLabels,
        classes
      );

      const secondaryModel =
        this.selectedSecondaryModelId != null
          ? this.models.find((m) => m.id === this.selectedSecondaryModelId)
          : null;
      const secondaryPredictedLabels = secondaryModel
        ? extractVideoLabelsFromModelPrediction(rawMedia, secondaryModel)
        : [];
      const secondaryModelIntervals = mapVideoLabelsToIntervals(
        secondaryPredictedLabels,
        classes
      );

      if (
        labelerIntervals.length === 0 &&
        modelIntervals.length === 0 &&
        secondaryModelIntervals.length === 0
      ) {
        runInAction(() => {
          this.incidentSeries = {
            classes,
            labelerIntervals: [],
            modelIntervals: [],
            secondaryModelIntervals: [],
          };
        });
        return;
      }

      runInAction(() => {
        this.incidentSeries = {
          classes,
          labelerIntervals,
          modelIntervals,
          secondaryModelIntervals,
        };
      });
    } catch (e) {
      runInAction(() => {
        const message = (e as Error)?.message;
        this.errorMessage =
          message || "Impossible de charger la comparaison des incidents";
        this.incidentSeries = {
          classes: this.incidentClasses,
          labelerIntervals: [],
          modelIntervals: [],
          secondaryModelIntervals: [],
        };
      });
    } finally {
      runInAction(() => {
        this.isLoadingSeries = false;
      });
    }
  };

  @computed get selectedProjectLabelingType(): string | null {
    if (this.selectedProjectId == null) return null;
    const project = this.projects.find((p) => p.id === this.selectedProjectId);
    return project?.labelingType ?? null;
  }

  @computed get isSelectedProjectVideo(): boolean {
    return isVideoLabelingType(this.selectedProjectLabelingType);
  }

  @computed get currentSeries(): AnalyseIncidentSeries | null {
    return this.incidentSeries;
  }

  @computed get currentVideo(): AnalyseVideo | null {
    if (this.selectedVideoId == null) return null;
    return this.videos.find((v) => v.id === this.selectedVideoId) || null;
  }

  // ---- Fallback mock data (used until the analytics API is ready) ----

  private buildFallbackProjects = (): AnalyseProject[] =>
    [
      {
        id: "9001",
        name: "Demo - Autoroute A1",
        labelingType: ANALYSE_INCIDENTS_SUPPORTED_LABELING_TYPE,
      },
      {
        id: "9002",
        name: "Demo - Peripherique",
        labelingType: ANALYSE_INCIDENTS_SUPPORTED_LABELING_TYPE,
      },
      {
        id: "9003",
        name: "Demo - Tunnel Frejus",
        labelingType: ANALYSE_INCIDENTS_SUPPORTED_LABELING_TYPE,
      },
    ];

  private buildFallbackBatches = (projectId: string): AnalyseBatch[] => {
    const parsed = parseInt(projectId, 10);
    const base = Number.isFinite(parsed)
      ? parsed
      : hashString(projectId) % 10000;
    return [
      { id: base * 10 + 1, name: "Lot Matin", projectId },
      { id: base * 10 + 2, name: "Lot Apres-midi", projectId },
      { id: base * 10 + 3, name: "Lot Nuit", projectId },
    ];
  };

  private buildFallbackVideos = (batchId: number): AnalyseVideo[] => {
    const rng = createRng(hashString(`videos-${batchId}`));
    return Array.from({ length: 4 }).map((_, idx) => {
      const durationExactSec = 40 + rng() * 80;
      return {
        id: `${batchId}-mock-${idx}`,
        name: `Media ${idx + 1}`,
        durationExactSec,
        durationSec: chartDurationSecFromExact(durationExactSec),
        fps: 25,
      };
    });
  };
}

export default AnalyseStore;
